import { PresenceTransition } from 'native-base';
import { JSX, ReactNode, useEffect, useRef } from 'react';
import { getStylesheet } from '../../styles';
import { Heading } from '../core';

type SlideTransitionProps = {
  back?: boolean;
  children: ReactNode;
  maxHeight?: number;
  screenWidth?: number;
  slideCount: number;
  slideNumber: number;
  visible?: boolean;
};

export const SlideTransition = ({
  children,
  maxHeight,
  slideCount,
  slideNumber,
  back = false,
  screenWidth = 500,
  visible = false,
}: SlideTransitionProps): JSX.Element => {
  const headingRef = useRef<Heading.HeadingRef>();

  useEffect(() => {
    if (visible) {
      headingRef.current?.focus?.();
    }
  }, [visible]);

  const enterInitial = { opacity: 0, translateX: !back ? -screenWidth : screenWidth } as const;
  const exitInitial = { opacity: 0, translateX: !back ? screenWidth : -screenWidth } as const;

  const animation = { opacity: 1, translateX: 0, transition: { duration: 300 } } as const;

  const style = {
    maxHeight,
    height: '100%',
    position: visible ? undefined : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: visible ? 1 : 0,
  } as const;

  const headingId = `slide-${slideNumber}-heading`;

  return (
    <PresenceTransition
      aria-labelledby={headingId}
      aria-hidden={!visible}
      role="group"
      visible={visible}
      initial={visible ? exitInitial : enterInitial}
      animate={animation}
      style={style}
      {...(visible ? {} : { tabIndex: -1 })}
    >
      <Heading.h6
        {...styles.visuallyHidden}
        id={headingId}
        level={2}
        ref={headingRef}
        tabIndex={-1}
      >
        {`Slide ${slideNumber} of ${slideCount}`}
      </Heading.h6>

      {children}
    </PresenceTransition>
  );
};

const styles = getStylesheet({
  visuallyHidden: {
    opacity: 0,
    height: 0,
  },
});
